import { useForm } from '@refinedev/antd';
import { Button, Flex, Form, Select, Typography } from 'antd';
import { PLATFORM_DATA_CONFIG } from 'common/constants';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'src/auth';
import { SelectWarehouse } from 'src/pages/orders-and-payments/electronic-invoice/electronic-invoice-link/electronic-invoice-purchase-order/purchase-order-due-date/purchase-order-due-date.style';
import { Organization } from 'src/pages/organization/organization.model';

import { showFifoConfirmationModal } from './fifo-confirmation-modal/fifo-confirmation-modal.component';
import {
  currencyList,
  languageMapByCountry,
} from './onboarding-start.constant';
import {
  OnboardingFormData,
  ValuationMethodOptionData,
} from './onboarding-start.model';
import * as Styled from './onboarding-start.style';
import { ValuationMethodOption } from './valuation-method-option/valuation-method-option.component';

const { Title, Text, Paragraph } = Typography;

const renderValuationMethodOption = (option: {
  data: ValuationMethodOptionData;
}) => (
  <ValuationMethodOption
    title={option.data.title}
    description={option.data.description}
  />
);

interface OnboardingStartViewProps {
  onFormSuccess?: () => void;
  organization: Organization | null;
}

const OnboardingStartView = ({
  onFormSuccess,
  organization,
}: OnboardingStartViewProps) => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const { formProps, saveButtonProps, form } = useForm<OnboardingFormData>({
    resource: 'onboarding/start',
    action: 'create',
    onMutationSuccess: () => {
      onFormSuccess?.();
    },
  });
  const regionName = new Intl.DisplayNames([i18n.language], { type: 'region' });
  const currencyName = new Intl.DisplayNames([i18n.language], {
    type: 'currency',
  });

  useEffect(() => {
    if (organization) {
      form?.setFieldsValue({
        country: organization.country,
        currency: organization.currency,
        organizationId: organization.id,
        language: languageMapByCountry[organization.country || ''] || 'pt-BR',
      });
    }
  }, [organization, form]);

  const handleValuationMethodChange = (value: string) => {
    if (value === 'FIFO') {
      showFifoConfirmationModal({
        t,
        onConfirm: () => {
          form?.setFieldValue('valuationMethod', 'FIFO');
        },
        onCancel: () => {
          form?.setFieldValue('valuationMethod', 'Moving Average');
        },
      });
    }
  };

  const valuationMethodOptions: ValuationMethodOptionData[] = [
    {
      label: t('onboarding.start.valuationMethod.movingAverage.label') || '',
      title: t('onboarding.start.valuationMethod.movingAverage.title') || '',
      description:
        t('onboarding.start.valuationMethod.movingAverage.description') || '',
      value: 'Moving Average',
    },
    {
      label: t('onboarding.start.valuationMethod.fifo.label') || '',
      title: t('onboarding.start.valuationMethod.fifo.title') || '',
      description: t('onboarding.start.valuationMethod.fifo.description') || '',
      value: 'FIFO',
    },
  ];

  return (
    <Styled.Container>
      <Styled.Header>
        <Styled.HeaderBackground />
      </Styled.Header>

      <Styled.ContentWrapper>
        <Styled.LeftSection>
          <Title level={4}>
            {t('onboarding.start.welcome', {
              name: user?.first_name || user?.name,
            })}
          </Title>

          <Paragraph>{t('onboarding.start.description')}</Paragraph>

          <Styled.AlertBanner
            message={t('onboarding.start.alert.title')}
            description={t('onboarding.start.alert.description')}
            type="info"
            showIcon
          />

          <Text type="secondary">{t('onboarding.start.footer')}</Text>
        </Styled.LeftSection>

        <Styled.RightSection>
          <Form
            {...formProps}
            layout="vertical"
            initialValues={{
              valuationMethod: 'Moving Average',
            }}
          >
            <Form.Item
              name="country"
              label={t('onboarding.start.country.label')}
              tooltip={t('onboarding.start.country.tooltip')}
            >
              <Select
                disabled
                size="large"
                options={
                  organization?.country
                    ? [
                        {
                          label: regionName.of(organization.country) as string,
                          value: organization.country,
                        },
                      ]
                    : []
                }
              />
            </Form.Item>

            <Form.Item
              name="currency"
              label={t('onboarding.start.currency.label')}
              tooltip={t('onboarding.start.currency.tooltip')}
            >
              <SelectWarehouse
                size="large"
                options={
                  currencyList.map((currency) => ({
                    label: `${currencyName.of(currency)} (${currency})`,
                    value: currency,
                  })) || []
                }
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>

            <Form.Item
              name="valuationMethod"
              label={t('onboarding.start.valuationMethod.label')}
              rules={[
                {
                  required: true,
                  message:
                    t('onboarding.start.valuationMethod.validation') || '',
                },
              ]}
            >
              <Select
                placeholder={t('onboarding.start.valuationMethod.placeholder')}
                size="large"
                options={valuationMethodOptions}
                optionRender={renderValuationMethodOption}
                onChange={handleValuationMethodChange}
              />
            </Form.Item>
            <Form.Item name="organizationId" hidden />
            <Form.Item name="language" hidden />
          </Form>

          <Flex gap="small" justify="flex-end">
            <Button
              size="large"
              onClick={() =>
                window.open(
                  `${PLATFORM_DATA_CONFIG.ACCOUNTS_URI}/app/workspaces?workspace=${organization?.workspace_id}&organization=${organization?.id}`,
                  '_blank',
                )
              }
            >
              {t('onboarding.start.buttons.goToAccount')}
            </Button>
            <Button size="large" type="primary" {...saveButtonProps}>
              {t('onboarding.start.buttons.continue')}
            </Button>
          </Flex>
        </Styled.RightSection>
      </Styled.ContentWrapper>
    </Styled.Container>
  );
};

export { OnboardingStartView };
export type { OnboardingStartViewProps };
